<template>
  <label :class="toggleClass">
    <input
      ref="inputRef"
      type="checkbox"
      class="toggle-input"
      :checked="modelValue"
      :disabled="disabled"
      @change="onChange"
    />
    <span class="toggle-checkbox">
      <Icon v-if="modelValue" name="check" :size="12" />
    </span>
    <span v-if="label || $slots.default" class="toggle-label">
      <slot>{{ label }}</slot>
    </span>
  </label>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import Icon from './Icon.vue';

export interface ToggleProps {
  modelValue: boolean;
  label?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const props = withDefaults(defineProps<ToggleProps>(), {
  disabled: false,
  size: 'medium',
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  change: [value: boolean];
}>();

const inputRef = ref<HTMLInputElement>();

const toggleClass = computed(() => [
  'toggle',
  `toggle--${props.size}`,
  {
    'toggle--disabled': props.disabled,
    'toggle--checked': props.modelValue,
  }
]);

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  emit('update:modelValue', target.checked);
  emit('change', target.checked);
};

defineExpose({
  focus: () => inputRef.value?.focus(),
  blur: () => inputRef.value?.blur(),
});
</script>

<style scoped lang="scss">
@use '@/styles/mixins';

.toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: var(--spacing-md);
  
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &--small {
    .toggle-checkbox {
      width: 14px;
      height: 14px;
    }
    
    .toggle-label {
      font-size: 12px;
    }
  }
  
  &--medium {
    .toggle-checkbox {
      width: 16px;
      height: 16px;
    }
    
    .toggle-label {
      font-size: var(--vscode-font-size);
    }
  }
  
  &--large {
    .toggle-checkbox {
      width: 18px;
      height: 18px;
    }
    
    .toggle-label {
      font-size: 14px;
    }
  }
}

.toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-checkbox {
  @include checkbox-style;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.1s ease;
  
  .toggle:hover:not(.toggle--disabled) & {
    border-color: var(--vscode-focusBorder);
  }
  
  .toggle-input:focus + & {
    @include focus-outline;
  }
  
  .toggle--checked & {
    background-color: var(--vscode-button-background);
    border-color: var(--vscode-button-background);
    color: var(--vscode-button-foreground);
  }
}

.toggle-label {
  font-family: var(--vscode-font-family);
  color: var(--vscode-foreground);
  line-height: var(--vscode-line-height);
  
  .toggle--disabled & {
    color: var(--vscode-foreground);
    opacity: 0.5;
  }
}
</style>
